<script setup lang="ts">
import {computed} from "vue";
import {CircleAlert} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import dateHelper from "@/core/helpers/dateHelper";
import {VoucherCheckResult} from "@/birthday-gift/interfaces";

/**
 * The code exists but cannot be honoured: already collected, or expired.
 */
const props = defineProps<{
    result: VoucherCheckResult | null;
    expired: boolean;
}>();

const emit = defineEmits<{
    (e: 'reset'): void;
}>();

const patient = computed(() => props.result?.patient);

const initials = computed(() => {
    const first = patient.value?.first_name?.charAt(0) ?? '';
    const last = patient.value?.last_name?.charAt(0) ?? '';

    return `${first}${last}`.toUpperCase();
});

const explanation = computed(() => {
    if (props.expired) {
        const until = props.result?.expires_at
            ? dateHelper.shortFormat(props.result.expires_at, 'D MMMM')
            : null;

        return until
            ? `Ce cadeau était à retirer avant le ${until}. Le code ${props.result?.code} n'est plus valable.`
            : `Le code ${props.result?.code} a expiré et n'est plus valable.`;
    }

    const when = props.result?.redeemed_at
        ? dateHelper.shortFormat(props.result.redeemed_at, 'D MMMM [à] HH:mm')
        : null;

    const by = props.result?.redeemed_by?.name;

    return [
        'Ce cadeau a déjà été retiré',
        when ? ` le ${when}` : '',
        by ? ` par ${by}` : '',
        `. Le code ${props.result?.code} est à usage unique : il n'est plus valable.`,
    ].join('');
});
</script>

<template>
  <div class="overflow-hidden rounded-xl border-2 border-solid border-orange">
    <div class="flex items-center gap-2.5 bg-orange px-4 py-2.5 text-sm font-semibold">
      <CircleAlert class="h-5 w-5" />{{ expired ? 'Ce cadeau a expiré' : 'Ce cadeau a déjà été remis' }}
    </div>

    <div class="flex flex-col gap-3.5 p-4">
      <div class="flex items-center gap-3">
        <span class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-light font-semibold text-muted-foreground">
          {{ initials }}
        </span>
        <div class="min-w-0">
          <div class="text-lg font-bold">
            {{ patient?.first_name }} {{ patient?.last_name }}
          </div>
          <div class="text-xs text-muted-foreground">
            {{ patient?.age }} ans
          </div>
        </div>
      </div>

      <p class="m-0 text-sm leading-relaxed text-gray-dark">
        {{ explanation }}
      </p>

      <div class="flex justify-end">
        <Button
          variant="ghost"
          @click="emit('reset')"
        >
          Scanner un autre code
        </Button>
      </div>
    </div>
  </div>
</template>
